import React, { useEffect, useRef, useState } from 'react';
import { ymcasAPI } from '../utils/api';
import { useShared } from '../sharedStyles';

const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};
export const stateName = (code) => STATE_NAMES[code] || code;

// State-first YMCA picker (v2.0.1, PO decision): choose the state, then a
// branch in that state (searchable), or type an unlisted YMCA. Emits
// { ymcaId, ymcaOther, label }.
const YmcaPicker = ({ value, onChange, required = true }) => {
  const shared = useShared();
  const [states, setStates] = useState([]);
  const [state, setState] = useState(value?.state || '');
  const [query, setQuery] = useState(value?.label || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [notListed, setNotListed] = useState(!!value?.ymcaOther);
  const [other, setOther] = useState(value?.ymcaOther || '');
  const timer = useRef(null);

  useEffect(() => {
    let active = true;
    ymcasAPI.states()
      .then((res) => { if (active) setStates(res.data.states || []); })
      .catch(() => { if (active) setStates([]); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (notListed || !state) { setResults([]); return undefined; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const res = await ymcasAPI.search(query.trim(), state);
        setResults(res.data.ymcas || []);
      } catch (e) {
        setResults([]);
      }
    }, 150);
    return () => clearTimeout(timer.current);
  }, [query, state, notListed]);

  const pick = (y) => {
    const label = `${y.name}${y.city ? ` — ${y.city}, ${y.state || ''}` : ''}`.trim();
    setQuery(label);
    setOpen(false);
    onChange({ ymcaId: y.id, ymcaOther: '', label, state });
  };

  const changeState = (code) => {
    setState(code);
    setQuery('');
    setOpen(false);
    onChange({ ymcaId: '', ymcaOther: notListed ? other : '', label: notListed ? other : '', state: code });
  };

  const switchNotListed = (on) => {
    setNotListed(on);
    if (on) onChange({ ymcaId: '', ymcaOther: other, label: other, state });
    else { setQuery(''); onChange({ ymcaId: '', ymcaOther: '', label: '', state }); }
  };

  return (
    <div>
      <label style={shared.label}>State{required ? '' : ' (optional)'}</label>
      <select style={shared.input} value={state} onChange={(e) => changeState(e.target.value)} aria-label="YMCA state">
        <option value="">Select your state…</option>
        {states.map((s) => (
          <option key={s.state} value={s.state}>{stateName(s.state)} ({s.count})</option>
        ))}
        {states.length > 0 && <option value="__other">My state isn't listed</option>}
      </select>

      {(state && state !== '__other' && !notListed) && (
        <div style={{ position: 'relative', marginTop: '10px' }}>
          <label style={shared.label}>Your YMCA</label>
          <input
            style={shared.input}
            placeholder={`Search ${stateName(state)} branches by name or city`}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); if (value?.ymcaId) onChange({ ymcaId: '', ymcaOther: '', label: '', state }); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            aria-label="Search YMCA directory"
            autoComplete="off"
          />
          {open && (
            <div style={styles.menu} role="listbox">
              {results.length === 0 ? (
                <div style={styles.emptyRow}>No matches in {stateName(state)}. Try a city, or choose "not listed" below.</div>
              ) : results.map((y) => (
                <div key={y.id} role="option" aria-selected={value?.ymcaId === y.id} style={styles.row} onMouseDown={() => pick(y)}>
                  <div style={{ fontWeight: 600, color: '#0D1117' }}>{y.name}</div>
                  <div style={{ fontSize: '11.5px', color: '#9AA0B0' }}>{[y.city, y.state].filter(Boolean).join(', ')}{y.association ? ` · ${y.association}` : ''}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {(notListed || state === '__other') && (
        <div style={{ marginTop: '10px' }}>
          <label style={shared.label}>YMCA name</label>
          <input
            style={shared.input}
            placeholder="Type your YMCA's name"
            value={other}
            onChange={(e) => { setOther(e.target.value); onChange({ ymcaId: '', ymcaOther: e.target.value, label: e.target.value, state }); }}
            aria-label="YMCA name"
          />
        </div>
      )}

      {state && state !== '__other' && (
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#5A6070', marginTop: '6px', cursor: 'pointer' }}>
          <input type="checkbox" checked={notListed} onChange={(e) => switchNotListed(e.target.checked)} />
          My YMCA isn't listed
        </label>
      )}
    </div>
  );
};

const styles = {
  menu: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid rgba(0,0,0,0.12)', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 50, maxHeight: '220px', overflowY: 'auto', marginTop: '4px' },
  row: { padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid rgba(0,0,0,0.04)' },
  emptyRow: { padding: '10px 12px', fontSize: '12.5px', color: '#9AA0B0' },
};

export default YmcaPicker;
